import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import TradeLogMiniStatistics from "./TradeLogMiniStatistics";
import SortableCard from "../../Dashboard/components/SortableCard";
import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useIsBelowLg, useIsMobile } from "@/hooks/use-mobile";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { Trade, TradeStatistics, BackendTradeStatistics } from "../../../../types/trade";
import { DateRange } from "../../../../types/trade";
import { DashboardHeaderDateRangePicker } from "../../Dashboard/components/DashboardHeaderDateRangePicker";
import type {
  DashboardDatePresetTimeframe,
  DashboardDateRangeChangeOptions,
} from "@/utils/dashboardDateWindowStorage";
import { DashboardHeaderExportButton } from "../../Dashboard/components/DashboardHeaderExportButton";
import { DashboardHeaderImportButton } from "../../Dashboard/components/DashboardHeaderImportButton";
import { NeedHelpButton } from "@/components/need-help-button";
import { PageRouteHeadline } from "@/components/Layout/PageRouteHeadline";
import { cn } from "@/lib/utils";
import { TradeLogShowStatsToggle } from "./TradeLogShowStatsToggle";
import {
  dashboardMiniStatsGridTemplateColumns,
  miniStatAmountNeedsFullWidthOnMobile,
  miniStatGridTemplateColumns,
} from "../../../../utils/miniStatGridLayout";

const TRADE_LOG_HELP_URL = "https://help.tesseratrades.com/trade-log";

/** Hidden below xl — core four stats only on mobile/tablet. */
const TRADE_LOG_SECONDARY_STAT_CARD_IDS = new Set([
  "averageWin",
  "averageLoss",
  "largestWin",
  "largestLoss",
]);

interface TradeLogHeaderProps {
  statistics: TradeStatistics;
  backendStats: BackendTradeStatistics | null;
  trades: Trade[];
  dateRange: DateRange;
  selectedPresetId?: DashboardDatePresetTimeframe | null;
  onDateRangeChange: (
    dateRange: DateRange,
    options?: DashboardDateRangeChangeOptions
  ) => void;
  onExport?: () => void;
  onImport?: () => void;
}

const TradeLogHeader: React.FC<TradeLogHeaderProps> = ({ 
  statistics, 
  backendStats,
  trades,
  dateRange,
  selectedPresetId = null,
  onDateRangeChange,
  onExport,
  onImport,
}) => {
  const { formatMoney } = useDisplayCurrency();
  const isMobile = useIsMobile();
  const isBelowLg = useIsBelowLg();
  const shadcnInset = useShadcnInsetChrome();
  const { mountEl: insetHeaderMountEl, setActions: setInsetHeaderActions } =
    useShadcnInsetHeaderActions();

  const brandColor = "#38BBD1";

  const [showStats, setShowStats] = useState<boolean>(() => {
    const saved = localStorage.getItem("trade_log_show_stats");
    return saved ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem("trade_log_show_stats", JSON.stringify(showStats));
  }, [showStats]);

  const defaultCardOrder = [
    "totalPnL",
    "winRate",
    "closedTrades",
    "openTrades",
    "averageWin",
    "averageLoss",
    "largestWin",
    "largestLoss",
  ];

  const normalizeCardOrderIds = (ids: string[]): string[] => {
    const mapped = ids.map((id) => (id === "totalTrades" ? "closedTrades" : id));
    const deduped: string[] = [];
    const seen = new Set<string>();
    for (const id of mapped) {
      if (seen.has(id)) continue;
      seen.add(id);
      deduped.push(id);
    }
    const filtered = deduped.filter((id: string) => defaultCardOrder.includes(id));
    const missing = defaultCardOrder.filter((id: string) => !filtered.includes(id));
    return [...filtered, ...missing].slice(0, 8);
  };

  const [cardOrder, setCardOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem("trade_log_card_order");
    if (saved) {
      try {
        const savedOrder: string[] = JSON.parse(saved);
        return normalizeCardOrderIds(savedOrder);
      } catch (e) {
        return defaultCardOrder;
      }
    }
    return defaultCardOrder;
  });

  useEffect(() => {
    const finalOrder = normalizeCardOrderIds(cardOrder);
    if (finalOrder.length !== cardOrder.length || finalOrder.some((id: string, idx: number) => id !== cardOrder[idx])) {
      setCardOrder(finalOrder);
    }
  }, []);

  useEffect(() => {
    const finalOrder = normalizeCardOrderIds(cardOrder);
    localStorage.setItem("trade_log_card_order", JSON.stringify(finalOrder));
  }, [cardOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setCardOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const formatPercent = (value: number): string => {
    return `${value.toFixed(2)}%`;
  };

  const getTradeLogStatAmount = (cardId: string): string => {
    const id = cardId === "totalTrades" ? "closedTrades" : cardId;
    switch (id) {
      case "openTrades":
        return `${statistics.openTrades} OPEN`;
      case "winRate":
        return formatPercent(statistics.winRate);
      case "totalPnL":
        return formatMoney(statistics.totalPnL);
      case "closedTrades":
        return statistics.closedTrades.toString();
      case "averageWin":
        return formatMoney(statistics.averageWin || 0);
      case "largestWin":
        return formatMoney(statistics.largestWin || 0);
      case "averageLoss":
        return formatMoney(statistics.averageLoss || 0);
      case "largestLoss":
        return statistics.largestLoss < 0 ? formatMoney(statistics.largestLoss) : "N/A";
      case "mostTraded":
        return statistics.mostTradedSymbol || "N/A";
      default:
        return "";
    }
  };

  const getTradeLogStatTitle = (cardId: string): string => {
    const id = cardId === "totalTrades" ? "closedTrades" : cardId;
    switch (id) {
      case "openTrades":
        return "Active Trades";
      case "winRate":
        return "Win Rate";
      case "totalPnL":
        return "Total P&L";
      case "closedTrades":
        return "Closed Trades";
      case "averageWin":
        return "Average Win";
      case "largestWin":
        return "Largest Win";
      case "averageLoss":
        return "Average Loss";
      case "largestLoss":
        return "Largest Loss";
      case "mostTraded":
        return "Most Traded";
      default:
        return "";
    }
  };

  const visibleCardIds = useMemo(
    () =>
      cardOrder
        .filter((cardId) => defaultCardOrder.includes(cardId))
        .slice(0, 8),
    [cardOrder, defaultCardOrder]
  );

  const miniStatsGridTemplateColumns = useMemo(() => {
    const items = visibleCardIds.map((id) => ({
      title: getTradeLogStatTitle(id),
      amount: getTradeLogStatAmount(id),
    }));

    if (isMobile) {
      return dashboardMiniStatsGridTemplateColumns(items, {
        isMobile: true,
        maxColumns: 4,
        gapPx: 24,
      });
    }

    const needsFullWidth = items.some(
      (item) => item.amount && miniStatAmountNeedsFullWidthOnMobile(item.amount),
    );

    return miniStatGridTemplateColumns(items, {
      maxColumns: 4,
      minColumns: isBelowLg && !needsFullWidth ? 2 : undefined,
      gapPx: 24,
    });
  }, [visibleCardIds, statistics, formatMoney, isMobile, isBelowLg]);

  const getClosedTradesPercentage = (): number | null => {
    if (!backendStats || backendStats.closed_trades_change_percent === null) return null;
    return backendStats.closed_trades_change_percent;
  };

  const getTotalPnLPercentage = (): number | null => {
    if (!backendStats) return null;
    if (backendStats.pnl_change_percent !== null) return backendStats.pnl_change_percent;
    if (backendStats.pnl_vs_previous_percent !== null) return backendStats.pnl_vs_previous_percent;
    return null;
  };

  const getWinRatePercentage = (): number | null => {
    if (!backendStats || backendStats.win_rate_change_percent === null) return null;
    return backendStats.win_rate_change_percent;
  };

  const renderCard = (cardId: string) => {
    const id = cardId === "totalTrades" ? "closedTrades" : cardId;
    switch (id) {
      case "openTrades":
        return (
          <TradeLogMiniStatistics
            title="Active Trades"
            amount={`${statistics.openTrades} OPEN`}
            percentage={0}
            backgroundGradient="linear-gradient(135deg, #F45B0B, #F49113)"
            showPercentage={false}
            fitAmount
            tooltip="The number of trades that are currently open and have not yet been closed. These positions are still active in the market."
          />
        );
      case "winRate":
        return (
          <TradeLogMiniStatistics
            title="Win Rate"
            amount={formatPercent(statistics.winRate)}
            percentage={0}
            showPercentage={false}
            backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
            fitAmount
            tooltip="The percentage of closed trades that resulted in a profit. Calculated as (Winning Trades / Total Closed Trades) × 100."
          />
        );
      case "totalPnL":
        return (
          <TradeLogMiniStatistics
            title="Total P&L"
            amount={formatMoney(statistics.totalPnL)}
            percentage={null}
            showPercentage={false}
            fitAmount
            tooltip="Total Profit and Loss. The sum of all realized gains and losses from closed trades. This represents your overall trading performance."
          />
        );
      case "closedTrades":
        return (
          <TradeLogMiniStatistics
            title="Closed Trades"
            amount={statistics.closedTrades.toString()}
            percentage={0}
            showPercentage={false}
            backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
            fitAmount
            tooltip="Number of closed trades in the selected date range (exit-based cohort). Open positions are not included—use Active Trades for those."
          />
        );
      case "averageWin":
        return (
          <TradeLogMiniStatistics
            title="Average Win"
            amount={formatMoney(statistics.averageWin || 0)}
            percentage={0}
            showPercentage={false}
            fitAmount
            tooltip="The average profit per winning trade. Calculated by dividing the total profit from all winning trades by the number of winning trades."
          />
        );
      case "largestWin":
        return (
          <TradeLogMiniStatistics
            title="Largest Win"
            amount={formatMoney(statistics.largestWin || 0)}
            percentage={0}
            showPercentage={false}
            fitAmount
            tooltip="The single largest profit from any closed trade. This represents your best-performing trade."
          />
        );
      case "averageLoss":
        return (
          <TradeLogMiniStatistics
            title="Average Loss"
            amount={formatMoney(statistics.averageLoss || 0)}
            percentage={null}
            showPercentage={false}
            fitAmount
            tooltip="The average loss per losing trade. Calculated by dividing the total loss from all losing trades by the number of losing trades."
          />
        );
      case "largestLoss":
        return (
          <TradeLogMiniStatistics
            title="Largest Loss"
            amount={statistics.largestLoss < 0 ? formatMoney(statistics.largestLoss) : "N/A"}
            percentage={null}
            backgroundGradient="linear-gradient(135deg, #E00101, #FE4244)"
            showPercentage={false}
            fitAmount
            tooltip="The single largest loss from any closed trade. This represents your worst-performing trade."
          />
        );
      case "mostTraded":
        return (
          <TradeLogMiniStatistics
            title="Most Traded"
            amount={statistics.mostTradedSymbol || "N/A"}
            percentage={0}
            backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
            showPercentage={false}
            fitAmount
            tooltip="The trading symbol (stock, forex pair, cryptocurrency, etc.) that you have traded the most frequently across all your trades."
          />
        );
      default:
        return null;
    }
  };

  const showStatsControlId = shadcnInset ? "show-stats-inset" : "show-stats";

  const pageActionToolbar = useMemo(
    () => (
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        {onExport ? <DashboardHeaderExportButton onClick={onExport} /> : null}
        {onImport ? <DashboardHeaderImportButton /> : null}
        <DashboardHeaderDateRangePicker
          dateRange={dateRange}
          selectedPresetId={selectedPresetId}
          onDateRangeChange={onDateRangeChange}
        />
        <NeedHelpButton href={TRADE_LOG_HELP_URL} />
      </div>
    ),
    [onExport, onImport, dateRange, selectedPresetId, onDateRangeChange],
  );

  const headerToolbar = useMemo(
    () => (
      <div className="flex flex-nowrap items-center gap-3">
        <TradeLogShowStatsToggle
          id={showStatsControlId}
          checked={showStats}
          onCheckedChange={setShowStats}
          hideOnMobile={shadcnInset}
          className={
            shadcnInset
              ? "[&_[data-slot=label]]:text-xs [&_[data-slot=label]]:uppercase [&_[data-slot=label]]:text-gray-500 dark:[&_[data-slot=label]]:!text-white"
              : undefined
          }
        />
        {pageActionToolbar}
      </div>
    ),
    [shadcnInset, showStatsControlId, showStats, pageActionToolbar],
  );

  const compactInsetHeaderToolbar = useMemo(
    () => (
      <div className="flex shrink-0 flex-nowrap items-center gap-3">
        <DashboardHeaderDateRangePicker
          dateRange={dateRange}
          selectedPresetId={selectedPresetId}
          onDateRangeChange={onDateRangeChange}
        />
        {onExport ? (
          <DashboardHeaderExportButton onClick={onExport} iconOnly={isBelowLg} />
        ) : null}
        {onImport ? <DashboardHeaderImportButton iconOnly={isBelowLg} /> : null}
        <NeedHelpButton href={TRADE_LOG_HELP_URL} iconOnly={isBelowLg} />
      </div>
    ),
    [onExport, onImport, dateRange, selectedPresetId, onDateRangeChange, isBelowLg],
  );

  const legacyHeaderToolbar = useMemo(
    () => (
      <div className="flex flex-nowrap items-center gap-3">
        <TradeLogShowStatsToggle
          id={showStatsControlId}
          checked={showStats}
          onCheckedChange={setShowStats}
        />
        <DashboardHeaderDateRangePicker
          dateRange={dateRange}
          selectedPresetId={selectedPresetId}
          onDateRangeChange={onDateRangeChange}
        />
        {onExport ? <DashboardHeaderExportButton onClick={onExport} /> : null}
        {onImport ? <DashboardHeaderImportButton /> : null}
        <NeedHelpButton href={TRADE_LOG_HELP_URL} />
      </div>
    ),
    [
      showStatsControlId,
      showStats,
      onExport,
      onImport,
      dateRange,
      selectedPresetId,
      onDateRangeChange,
    ],
  );

  useEffect(() => {
    if (!shadcnInset || isBelowLg) {
      setInsetHeaderActions(null);
      return;
    }
    setInsetHeaderActions(headerToolbar);
    return () => setInsetHeaderActions(null);
  }, [shadcnInset, isBelowLg, headerToolbar, setInsetHeaderActions]);

  return (
    <div className="mb-6 flex flex-col">
      {shadcnInset && isBelowLg && insetHeaderMountEl
        ? createPortal(compactInsetHeaderToolbar, insetHeaderMountEl)
        : null}
      {!shadcnInset ? (
        <div className="mb-6 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <PageRouteHeadline color={brandColor}>Trade Log</PageRouteHeadline>
          <div className="flex w-full flex-col items-start gap-4 md:w-auto md:flex-row md:items-center">
            <div className="w-full md:w-auto">{legacyHeaderToolbar}</div>
          </div>
        </div>
      ) : null}

      {showStats && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={cardOrder} strategy={rectSortingStrategy}>
            <div
              data-trade-log-mini-stats-grid=""
              data-mini-stats-grid=""
              className="grid gap-3 sm:gap-4 md:gap-6"
              style={{ gridTemplateColumns: miniStatsGridTemplateColumns }}
            >
              {visibleCardIds.map((cardId) => (
                  <div
                    key={cardId}
                    className={cn(
                      "min-w-0",
                      TRADE_LOG_SECONDARY_STAT_CARD_IDS.has(cardId) && "hidden xl:block",
                    )}
                  >
                    <SortableCard id={cardId} items={cardOrder}>
                      {renderCard(cardId)}
                    </SortableCard>
                  </div>
                ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default TradeLogHeader;
